import React from 'react';
import PropTypes from 'prop-types';
import { getDataFromTree } from '@apollo/client/react/ssr';
import initApollo from './init-Apollo';
import Bugsnag from './bugsnag';
import { getLocaleOnSSR } from './helpers/getLocaleOnSSR';
import { getSSRContextData } from './helpers/ssrContext';
import { getI18nForLang } from './helpers/getI18nForLang';
import { getLocaleFromBrowser } from './helpers';
import CONFIG from '../config';

// Get language from request (server) or hostname (client)
function getRequestLanguage(req) {
  if (!process.browser && req) {
    return getLocaleOnSSR(req);
  }

  if (typeof window !== 'undefined') {
    return getLocaleFromBrowser();
  }

  return null;
}

const withApolloClient = App =>
  class Apollo extends React.Component {
    static displayName = 'withApollo(App)';

    static async getInitialProps(ctx) {
      const {
        Component,
        router,
        ctx: { res, AppTree, req }
      } = ctx;

      // Get language from request (server) or hostname (client)
      // This prevents race conditions when multiple SSR requests run concurrently
      const lng = getRequestLanguage(req) || CONFIG.defaultLocale;

      // Get thread-safe i18n instance for this specific language
      // Each language has its own cached instance, preventing cross-request contamination
      const requestI18n = getI18nForLang(lng);

      // Store the request-scoped i18n in context for child components
      ctx.ctx.requestI18n = requestI18n;
      ctx.ctx.lng = lng;

      const { platform, userAgent } = getSSRContextData(req);

      // Run all GraphQL queries in the component tree
      // and extract the resulting data
      const apollo = initApollo(
        {},
        {
          lang: lng,
          platform,
          userAgent
        }
      );

      ctx.ctx.apolloClient = apollo;

      let appProps = {};
      if (App.getInitialProps) {
        appProps = await App.getInitialProps(ctx);
      }

      if (res && res.finished) {
        // When redirecting, the response is finished.
        // No point in continuing to render
        return {};
      }

      if (!process.browser) {
        try {
          // Run all GraphQL queries
          await getDataFromTree(
            <AppTree
              {...appProps}
              Component={Component}
              router={router}
              apolloClient={apollo}
            />
          );
        } catch (error) {
          // Prevent Apollo Client GraphQL errors from crashing SSR.
          // Handle them in components via the data.error prop:
          // https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-query-data-error
          console.error('Error while running `getDataFromTree`', error);
          Bugsnag.notify(error);
        }
      }

      // Extract query data from the Apollo store
      const apolloState = apollo.cache.extract();

      return {
        ...appProps,
        apolloState,
        lng
      };
    }

    static propTypes = {
      apolloState: PropTypes.object,
      bridgeState: PropTypes.object,
      lng: PropTypes.string
    };

    constructor(props) {
      super(props);
      // Determine initial language
      let lang = props.lng;
      if (!lang && process.browser && typeof window !== 'undefined') {
        lang = getLocaleFromBrowser();
      }
      lang = lang || CONFIG.defaultLocale;

      this.apolloClient = initApollo(props.apolloState, { lang });
    }

    render() {
      // Always get current client from initApollo - it handles language changes
      const lang = process.browser ? getLocaleFromBrowser() : this.props.lng;
      const client = initApollo(this.props.apolloState, { lang });
      return <App {...this.props} apolloClient={client} />;
    }
  };

export default withApolloClient;
